import { buildAdminV2CommunicationSelfTestEndpoints } from "./adminV2CommunicationSelfTestEndpoints";
import { buildAdminV2MessagingMergeFallbackEndpoints } from "./adminV2MessagingMergeFallbackEndpoints";
import { buildAdminV2PersonalDraftEndpoints } from "./adminV2PersonalDraftEndpoints";
import { optionalQueryParams } from "../apiEndpointHelpers";
import type { ApiEndpointBuilder } from "../apiEndpointHelpers";
import { buildAdminV2MessagingEmailAuthoringEndpoints } from "./adminV2MessagingEmailAuthoringEndpoints";
import { buildAdminV2MessagingEmailTrainingEndpoints } from "./adminV2MessagingEmailTrainingEndpoints";
import { buildAdminV2MessagingSignatureEndpoints } from "./adminV2MessagingSignatureEndpoints";
import { buildAdminV2MessagingTemplateEndpoints } from "./adminV2MessagingTemplateEndpoints";
import type { AdminV2MessagingTemplateDraftInput } from "./adminV2MessagingTemplateEndpointTypes";
import { buildAdminV2MessagingTemplateFolderEndpoints } from "./adminV2MessagingTemplateFolderEndpoints";
import { buildAdminV2MessagingTemplateFormEndpoints } from "./adminV2MessagingTemplateFormEndpoints";
import { buildAdminV2MessagingTemplateHelpEndpoints } from "./adminV2MessagingTemplateHelpEndpoints";
import { buildAdminV2MessagingTemplatePlacementEndpoints } from "./adminV2MessagingTemplatePlacementEndpoints";

import type {
  AdminV2AutomationReviewEmailContext,
  AdminV2BroadcastRecipientPage,
  AdminV2CreateMergeTagRequest,
  AdminV2EmailActivityAttachmentDownloadResponse,
  AdminV2EmailActivityHistoryContactCandidate,
  AdminV2EmailActivityResponse,
  AdminV2EmailActivitySourceKey,
  AdminV2EmailActivityStatusFilter,
  AdminV2EmailAssistantContextWindowResponse,
  AdminV2EmailHistoryResponse,
  AdminV2EmailMessagesResponse,
  AdminV2InAppMessagesResponse,
  JsonValue as AdminV2JsonValue,
  AdminV2LiveAlertMutationResponse,
  AdminV2ManualEmailJobContextResponse,
  AdminV2ManualEmailJobCreateResponse,
  AdminV2ManualEmailJobPreviewResponse,
  AdminV2ManualEmailJobRequestBody,
  AdminV2MessageEventType,
  AdminV2MessagingCategory,
  AdminV2MessagingChannel,
  AdminV2MessagingGlobalEmailDesignSettings,
  AdminV2MessagingGlobalEmailDesignUpdateInput,
  AdminV2MessagingSendMessageResponse,
  AdminV2MessagingSettingsReadinessResponse,
  AdminV2MessagingTemplateSelfTestPreviewResponse,
  AdminV2MessagingTemplateSelfTestSendResponse,
  AdminV2PersonCommunicationPreferencesResponse,
  AdminV2SenderIdentitiesResponse,
  AdminV2SenderIdentityCategory,
  AdminV2SenderIdentityLookup,
  AdminV2SenderIdentityVerificationStatus,
  AdminV2SenderReplyRouting,
  AdminV2UpdateCommunicationPreferenceRequest,
  AdminV2UpdateCommunicationPreferenceResponse,
} from "@/types/adminV2";
import type { AdminV2CommunicationContactScopeSelection } from "@/types/adminV2/communicationContactScope";
import type { AdminV2CommunicationOutcome } from "@/types/adminV2/communicationOutcome";
import type { AdminV2CommunicationReview } from "@/types/adminV2/communicationReview";
import type { CommunicationSignature } from "@/types/adminV2/communicationSignature";
import type {
  CommunicationTemplateDraft,
  CommunicationTemplateSource,
} from "@/types/adminV2/communicationTemplate";

/**
 * Admin V2 messaging endpoint definitions extracted from baseApi.ts.
 *
 * @remarks
 * Keeps generated hooks and cache-tag ids on the shared apiSlice while moving
 * messaging-specific request ownership out of the central API file.
 *
 * @see docs/admin/README.md - admin surface and API integration ownership
 */
export const buildAdminV2MessagingEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  ...buildAdminV2CommunicationSelfTestEndpoints(builder),
  ...buildAdminV2MessagingMergeFallbackEndpoints(builder),
  ...buildAdminV2PersonalDraftEndpoints(builder),
  getAdminV2CommunicationTemplateSource: builder.query<
    CommunicationTemplateSource,
    { templateId: string; ownerKey: string }
  >({
    query: ({ templateId }) => ({
      url: `/admin/v2/messaging/templates/${templateId}/communication-copy`,
    }),
    providesTags: (_result, _error, { templateId }) => [
      { type: "AdminV2MessagingTemplates", id: templateId },
    ],
    keepUnusedDataFor: 0,
  }),
  reconcileAdminV2MessagingMessage: builder.mutation<
    { outcome: AdminV2CommunicationOutcome | null },
    { requestKey: string; organisationId: string | null }
  >({
    query: (body) => ({
      url: "/admin/v2/messaging/messages/reconcile",
      method: "POST",
      data: body,
    }),
  }),
  getAdminV2MessagingSenderIdentities: builder.query<
    AdminV2SenderIdentitiesResponse,
    {
      organisationId?: string;
      globalOnly?: boolean;
      includeArchived?: boolean;
      q?: string;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/messaging/sender-identities",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2MessagingSenderIdentities", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  getAdminV2MessagingSenderIdentity: builder.query<
    AdminV2SenderIdentityLookup,
    { senderIdentityId: string; organisationId: string }
  >({
    query: ({ senderIdentityId, organisationId }) => ({
      url: `/admin/v2/messaging/sender-identities/${senderIdentityId}`,
      params: { organisationId },
    }),
    providesTags: (_result, _error, { senderIdentityId }) => [
      { type: "AdminV2MessagingSenderIdentities", id: senderIdentityId },
      { type: "AdminV2MessagingSenderIdentities", id: "LIST" },
    ],
    keepUnusedDataFor: 0,
  }),

  getAdminV2MessagingAutomationReviewContext: builder.query<
    AdminV2AutomationReviewEmailContext,
    { personId: string; sourceRecordId: string; automationRunId: string }
  >({
    query: ({ personId, sourceRecordId, automationRunId }) => ({
      url: `/admin/v2/messaging/email-assistant/context/people/${personId}/automation-review/${sourceRecordId}`,
      params: { automationRunId },
    }),
    keepUnusedDataFor: 0,
  }),

  getAdminV2MessagingSettingsReadiness: builder.query<
    AdminV2MessagingSettingsReadinessResponse,
    {
      organisationId?: string;
      senderIdentityId?: string;
      suppressAccessDeniedModal?: boolean;
    } | void
  >({
    query: (params) => {
      const { suppressAccessDeniedModal, ...queryParams } = params ?? {};
      return {
        url: "/admin/v2/messaging/settings/readiness",
        params: params ? optionalQueryParams(queryParams) : undefined,
        ...(suppressAccessDeniedModal
          ? { suppressAccessDeniedModal: true }
          : {}),
      };
    },
    providesTags: [
      { type: "AdminV2MessagingSettings", id: "GLOBAL_DESIGN" },
      { type: "AdminV2MessagingSenderIdentities", id: "LIST" },
      { type: "AdminV2MessagingTemplates", id: "LIST" },
      { type: "AdminV2MessagingEmailActivity", id: "LIST" },
    ],
    keepUnusedDataFor: 20,
  }),

  updateAdminV2MessagingGlobalEmailDesign: builder.mutation<
    AdminV2MessagingGlobalEmailDesignSettings,
    AdminV2MessagingGlobalEmailDesignUpdateInput
  >({
    query: (body) => ({
      url: "/admin/v2/messaging/settings/global-email-design",
      method: "PUT",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2MessagingSettings", id: "GLOBAL_DESIGN" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  createAdminV2MessagingSenderIdentity: builder.mutation<
    AdminV2SenderIdentitiesResponse["identities"][number],
    {
      organisationId?: string;
      displayName: string;
      email: string;
      category?: AdminV2SenderIdentityCategory;
      verificationStatus?: AdminV2SenderIdentityVerificationStatus;
      verificationError?: string | null;
      postmarkMessageStream: string;
      replyRouting?: AdminV2SenderReplyRouting;
      metadata?: AdminV2JsonValue;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/messaging/sender-identities",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2MessagingSenderIdentities", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  updateAdminV2MessagingSenderIdentity: builder.mutation<
    AdminV2SenderIdentitiesResponse["identities"][number],
    {
      senderIdentityId: string;
      body: {
        displayName?: string;
        email?: string;
        category?: AdminV2SenderIdentityCategory;
        verificationStatus?: AdminV2SenderIdentityVerificationStatus;
        verificationError?: string | null;
        postmarkMessageStream?: string;
        replyRouting?: AdminV2SenderReplyRouting;
        metadata?: AdminV2JsonValue;
      };
    }
  >({
    query: ({ senderIdentityId, body }) => ({
      url: `/admin/v2/messaging/sender-identities/${senderIdentityId}`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { senderIdentityId }) => [
      { type: "AdminV2MessagingSenderIdentities", id: "LIST" },
      {
        type: "AdminV2MessagingSenderIdentities",
        id: senderIdentityId,
      },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  archiveAdminV2MessagingSenderIdentity: builder.mutation<
    AdminV2SenderIdentitiesResponse["identities"][number],
    { senderIdentityId: string }
  >({
    query: ({ senderIdentityId }) => ({
      url: `/admin/v2/messaging/sender-identities/${senderIdentityId}/archive`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { senderIdentityId }) => [
      { type: "AdminV2MessagingSenderIdentities", id: "LIST" },
      {
        type: "AdminV2MessagingSenderIdentities",
        id: senderIdentityId,
      },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  ...buildAdminV2MessagingEmailAuthoringEndpoints(builder),
  ...buildAdminV2MessagingEmailTrainingEndpoints(builder),
  ...buildAdminV2MessagingTemplateEndpoints(builder),
  ...buildAdminV2MessagingTemplateHelpEndpoints(builder),
  ...buildAdminV2MessagingTemplateFormEndpoints(builder),
  ...buildAdminV2MessagingSignatureEndpoints(builder),
  ...buildAdminV2MessagingTemplateFolderEndpoints(builder),
  ...buildAdminV2MessagingTemplatePlacementEndpoints(builder),

  previewAdminV2MessagingTemplateSelfTest: builder.mutation<
    AdminV2MessagingTemplateSelfTestPreviewResponse,
    {
      templateId: string;
      body: {
        version: "draft" | "published";
        draft?: AdminV2MessagingTemplateDraftInput;
        expectedDraftVersionId?: string;
        personId: string;
        organisationId: string;
        recipientEmail?: string;
        prefixSubject?: boolean;
      };
    }
  >({
    query: ({ templateId, body }) => ({
      url: `/admin/v2/messaging/templates/${templateId}/test-send/preview`,
      method: "POST",
      data: body,
    }),
  }),

  sendAdminV2MessagingTemplateSelfTest: builder.mutation<
    AdminV2MessagingTemplateSelfTestSendResponse,
    {
      templateId: string;
      body: {
        version: "draft" | "published";
        draft?: AdminV2MessagingTemplateDraftInput;
        expectedDraftVersionId?: string;
        personId: string;
        organisationId: string;
        recipientEmail?: string;
        prefixSubject?: boolean;
        idempotencyKey: string;
        confirmedTemplateSnapshot: {
          deliveryMode: "api_test" | "staff_inbox";
          templateVersionId: string;
          globalDesignVersionId: string;
          contentDigest: string;
          renderDigest: string;
          overrideDigest: string;
          unsavedDraftDigest?: string;
          personId: string;
          organisationId: string;
          subject: string;
          recipientEmail?: string;
          prefixSubject?: boolean;
        };
      };
    }
  >({
    query: ({ templateId, body }) => ({
      url: `/admin/v2/messaging/templates/${templateId}/test-send`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2AuditLog", id: "LIST" },
      { type: "AdminV2MessagingEmailActivity", id: "LIST" },
    ],
  }),

  createAdminV2MessagingMergeTagRequest: builder.mutation<
    AdminV2LiveAlertMutationResponse,
    AdminV2CreateMergeTagRequest
  >({
    query: (body) => ({
      url: "/admin/v2/messaging/merge-tag-requests",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2LiveAlerts", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  reviewAdminV2MessagingMessage: builder.mutation<
    { review: AdminV2CommunicationReview },
    {
      body: {
        channel: "email";
        category: AdminV2MessagingCategory;
        personId: string;
        organisationId?: string | null;
        contactScope?: AdminV2CommunicationContactScopeSelection;
        senderIdentityId: string;
        subject: string;
        bodyText?: string;
        bodyMarkdown?: string;
        communicationTemplate?: CommunicationTemplateDraft;
        communicationSignature?: CommunicationSignature;
        idempotencyKey: string;
        recipientAddressSource?: "communication" | "login";
        templateId?: string;
      };
    }
  >({
    query: ({ body }) => ({
      url: "/admin/v2/messaging/messages/review",
      method: "POST",
      data: body,
    }),
  }),

  sendAdminV2MessagingMessage: builder.mutation<
    AdminV2MessagingSendMessageResponse,
    {
      body: {
        channel: AdminV2MessagingChannel;
        category: AdminV2MessagingCategory;
        recipientEmail?: string;
        personId?: string;
        organisationId?: string | null;
        contactScope?: AdminV2CommunicationContactScopeSelection;
        senderIdentityId?: string;
        templateId?: string;
        communicationTemplate?: CommunicationTemplateDraft;
        communicationSignature?: CommunicationSignature;
        templateVersion?: "draft" | "published";
        reviewedCommunicationDigest?: string;
        confirmedTemplateSnapshot?: {
          templateVersionId: string;
          globalDesignVersionId: string;
          contentDigest: string;
          renderDigest: string;
          overrideDigest: string;
          personId: string;
          organisationId: string;
          recipientEmail: string;
          subject: string;
          formLinkContext?: { messageRecipientId: string; proof: string };
        };
        variables?: AdminV2JsonValue;
        subject?: string;
        bodyMarkdown?: string;
        bodyRenderedHtml?: string;
        bodyText?: string;
        postmarkTag?: string;
        trackOpens?: boolean;
        trackLinks?: "None" | "HtmlAndText" | "HtmlOnly" | "TextOnly";
        attachments?: Array<{
          fileName: string;
          contentType: string;
          contentId?: string | null;
          sizeBytes?: number | null;
          base64Content?: string | null;
          storageProvider?: "s3" | "external" | "metadata_only";
          storageBucket?: string | null;
          storageKey?: string | null;
          externalUrl?: string | null;
          checksumSha256?: string | null;
          metadata?: AdminV2JsonValue;
        }>;
        metadata?: AdminV2JsonValue;
        reviewedCustomerSendProof?: {
          activityAuditReady: true;
          category?: AdminV2MessagingCategory;
          channel?: "email";
          finalConfirmation: true;
          operatorEnabled: true;
          preferenceChecked: true;
          recipientPreviewReviewed: true;
          recipients: string[];
          senderIdentityId: string;
          suppressionChecked: true;
          templateId?: string | null;
        };
        sourceIssueReportId?: string;
        idempotencyKey: string;
      };
    }
  >({
    query: ({ body }) => ({
      url: "/admin/v2/messaging/messages/send",
      method: "POST",
      data: body,
    }),
    invalidatesTags: (_result, _error, { body }) => [
      { type: "AdminV2AuditLog", id: "LIST" },
      { type: "AdminV2MessagingEmailActivity", id: "LIST" },
      { type: "AdminV2MessagingEmail", id: "history" },
      ...(body.personId
        ? [
            {
              type: "AdminV2MessagingEmailActivity" as const,
              id: `EMAIL_ASSISTANT_CONTEXT_${body.personId}`,
            },
          ]
        : []),
      ...(body.personId && body.organisationId
        ? [
            {
              type: "AdminV2CrmTimeline" as const,
              id: `crm_person:${body.personId}:${body.organisationId}`,
            },
          ]
        : []),
      ...(body.organisationId
        ? [
            {
              type: "AdminV2CrmTimeline" as const,
              id: `crm_business:${body.organisationId}:${body.organisationId}`,
            },
          ]
        : []),
    ],
  }),

  createAdminV2MessagingManualEmailJob: builder.mutation<
    AdminV2ManualEmailJobCreateResponse,
    {
      body: AdminV2ManualEmailJobRequestBody;
    }
  >({
    query: ({ body }) => ({
      url: "/admin/v2/messaging/manual-email-jobs",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2AuditLog", id: "LIST" },
      { type: "AdminV2Jobs", id: "LIST" },
      { type: "AdminV2MessagingEmailActivity", id: "LIST" },
    ],
  }),

  saveAdminV2MessagingManualEmailJobDraft: builder.mutation<
    AdminV2ManualEmailJobCreateResponse,
    {
      body: AdminV2ManualEmailJobRequestBody;
    }
  >({
    query: ({ body }) => ({
      url: "/admin/v2/messaging/manual-email-jobs/drafts",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2AuditLog", id: "LIST" },
      { type: "AdminV2Jobs", id: "LIST" },
    ],
  }),

  previewAdminV2MessagingManualEmailJob: builder.query<
    AdminV2ManualEmailJobPreviewResponse,
    {
      body: AdminV2ManualEmailJobRequestBody;
    }
  >({
    query: ({ body }) => ({
      url: "/admin/v2/messaging/manual-email-jobs/preview",
      method: "POST",
      data: body,
    }),
  }),

  getAdminV2MessagingManualEmailJobContext: builder.query<
    AdminV2ManualEmailJobContextResponse,
    {
      body: AdminV2ManualEmailJobRequestBody;
    }
  >({
    query: ({ body }) => ({
      url: "/admin/v2/messaging/manual-email-jobs/context",
      method: "POST",
      data: body,
    }),
    keepUnusedDataFor: 10,
  }),

  getAdminV2MessagingPersonPreferences: builder.query<
    AdminV2PersonCommunicationPreferencesResponse,
    { personId: string }
  >({
    query: ({ personId }) => ({
      url: `/admin/v2/messaging/preferences/people/${personId}`,
    }),
    providesTags: (_result, _error, { personId }) => [
      { type: "AdminV2MessagingPreferences", id: personId },
    ],
    keepUnusedDataFor: 10,
  }),

  updateAdminV2MessagingPersonPreference: builder.mutation<
    AdminV2UpdateCommunicationPreferenceResponse,
    {
      personId: string;
      body: AdminV2UpdateCommunicationPreferenceRequest;
    }
  >({
    query: ({ personId, body }) => ({
      url: `/admin/v2/messaging/preferences/people/${personId}`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { personId }) => [
      { type: "AdminV2MessagingPreferences", id: personId },
    ],
  }),

  getAdminV2MessagingEmailAssistantContext: builder.query<
    AdminV2EmailAssistantContextWindowResponse,
    {
      personId: string;
      contactScope?: AdminV2CommunicationContactScopeSelection;
    }
  >({
    query: ({ personId, contactScope }) => ({
      url: `/admin/v2/messaging/email-assistant/context/people/${personId}`,
      ...(contactScope
        ? { params: { contactScope: JSON.stringify(contactScope) } }
        : {}),
    }),
    providesTags: (_result, _error, { personId }) => [
      {
        type: "AdminV2MessagingEmailActivity",
        id: `EMAIL_ASSISTANT_CONTEXT_${personId}`,
      },
    ],
    keepUnusedDataFor: 10,
  }),

  getAdminV2MessagingPersonInAppMessages: builder.query<
    AdminV2InAppMessagesResponse,
    {
      personId: string;
      unreadOnly?: boolean;
      limit?: number;
      offset?: number;
    }
  >({
    query: ({ personId, ...params }) => ({
      url: `/admin/v2/messaging/in-app/people/${personId}`,
      params,
    }),
    providesTags: (_result, _error, { personId }) => [
      { type: "AdminV2MessagingInApp", id: personId },
    ],
    keepUnusedDataFor: 10,
  }),

  getAdminV2MessagingPersonEmailMessages: builder.query<
    AdminV2EmailMessagesResponse,
    {
      personId: string;
      limit?: number;
      offset?: number;
    }
  >({
    query: ({ personId, ...params }) => ({
      url: `/admin/v2/messaging/email/people/${personId}`,
      params,
    }),
    providesTags: (_result, _error, { personId }) => [
      { type: "AdminV2MessagingEmail", id: personId },
    ],
    keepUnusedDataFor: 10,
  }),

  getAdminV2MessagingEmailHistory: builder.query<
    AdminV2EmailHistoryResponse,
    {
      limit?: number;
      offset?: number;
      tag?: string;
      status?: string;
      source?: string;
      kind?: string;
      search?: string;
    }
  >({
    query: (params) => ({
      url: "/admin/v2/messaging/email",
      params,
    }),
    providesTags: [{ type: "AdminV2MessagingEmail", id: "history" }],
    keepUnusedDataFor: 10,
  }),

  getAdminV2MessagingEmailActivity: builder.query<
    AdminV2EmailActivityResponse,
    {
      campaignId?: string;
      q?: string;
      organisationId?: string;
      status?: AdminV2EmailActivityStatusFilter;
      eventType?: AdminV2MessageEventType;
      category?: AdminV2MessagingCategory;
      source?: AdminV2EmailActivitySourceKey;
      postmarkTag?: string;
      messageStream?: string;
      providerRoute?: string;
      providerMessageId?: string;
      recipientEmail?: string;
      hasAttachments?: boolean;
      dateFrom?: string;
      dateTo?: string;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/messaging/email-activity",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2MessagingEmailActivity", id: "LIST" }],
    keepUnusedDataFor: 10,
  }),

  getAdminV2MessagingBroadcastRecipients: builder.query<
    AdminV2BroadcastRecipientPage,
    { recipientId: string; cursor?: string }
  >({
    query: ({ recipientId, cursor }) => ({
      url: `/admin/v2/messaging/email-activity/recipients/${recipientId}/broadcast-recipients`,
      params: cursor ? { cursor } : undefined,
    }),
    keepUnusedDataFor: 0,
  }),

  getAdminV2MessagingEmailActivityDetail: builder.query<
    AdminV2EmailActivityResponse["items"][number],
    { recipientId: string }
  >({
    query: ({ recipientId }) => ({
      url: `/admin/v2/messaging/email-activity/recipients/${recipientId}`,
    }),
    providesTags: (_result, _error, { recipientId }) => [
      { type: "AdminV2MessagingEmailActivity", id: recipientId },
    ],
    keepUnusedDataFor: 60,
  }),

  getAdminV2MessagingEmailActivityHistoryContactCandidates: builder.query<
    { items: AdminV2EmailActivityHistoryContactCandidate[] },
    { recipientId: string; q: string }
  >({
    query: ({ recipientId, q }) => ({
      url: `/admin/v2/messaging/email-activity/recipients/${encodeURIComponent(recipientId)}/history-contact-candidates`,
      params: q.trim() ? { q: q.trim() } : undefined,
    }),
    keepUnusedDataFor: 0,
  }),

  linkAdminV2MessagingEmailActivityHistoryContact: builder.mutation<
    void,
    {
      recipientId: string;
      contactId: string;
      selectionToken: string;
      confirmDifferentEmail: boolean;
    }
  >({
    query: ({ recipientId, ...body }) => ({
      url: `/admin/v2/messaging/email-activity/recipients/${encodeURIComponent(recipientId)}/history-contact-link`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: (_result, error, { recipientId }) =>
      error
        ? []
        : [
            { type: "AdminV2MessagingEmailActivity", id: recipientId },
            { type: "AdminV2MessagingEmailActivity", id: "LIST" },
            { type: "AdminV2AuditLog", id: "LIST" },
          ],
  }),

  createAdminV2MessagingEmailActivityAttachmentDownloadLink: builder.mutation<
    AdminV2EmailActivityAttachmentDownloadResponse,
    { recipientId: string; attachmentId: string }
  >({
    query: ({ recipientId, attachmentId }) => ({
      url: `/admin/v2/messaging/email-activity/recipients/${recipientId}/attachments/${attachmentId}/download-link`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: [{ type: "AdminV2AuditLog", id: "LIST" }],
  }),

  markAdminV2MessagingInAppMessageRead: builder.mutation<
    AdminV2InAppMessagesResponse["items"][number]["recipient"],
    {
      personId: string;
      messageRecipientId: string;
    }
  >({
    query: ({ messageRecipientId }) => ({
      url: `/admin/v2/messaging/in-app/${messageRecipientId}/read`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { personId, messageRecipientId }) => [
      { type: "AdminV2MessagingInApp", id: personId },
      { type: "AdminV2MessagingInApp", id: messageRecipientId },
    ],
  }),
});
